import React, { useEffect, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { logSessionInfo } from '../../../../../services/logger'
import './AudioSection.scss'

/**
 * AudioSection component that renders an interactive waveform player using wavesurfer.js.
 * Displays a gray waveform with green progress indicator, play/pause controls,
 * and allows clicking to seek within the audio.
 * 
 * @param {Object} props - Component props
 * @param {string} props.audioUrl - URL of the audio file to load
 * @param {boolean} props.isLoading - Whether the audio is currently loading
 * @param {boolean} props.audioError - Whether an error occurred loading the audio
 * @param {Function} props.handleAudioError - Callback when audio loading fails
 * @param {Function} props.onPlayAudio - Callback when audio playback starts (optional)
 */
export default function AudioSection(props) {
    const { 
      audioUrl, 
      isLoading, 
      audioError, 
      handleAudioError,
      onPlayAudio
  } = props

    const waveformRef = useRef(null)
    const wavesurferRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isReady, setIsReady] = useState(false)

    // Use ref for callback to avoid WaveSurfer recreation when callback reference changes.
    // Without this, WaveSurfer is destroyed and recreated on every parent re-render
    // if handleAudioError is not memoized.
    const handleAudioErrorRef = useRef(handleAudioError)
    handleAudioErrorRef.current = handleAudioError

    const onPlayAudioRef = useRef(onPlayAudio)
    onPlayAudioRef.current = onPlayAudio

    useEffect(() => {
      if (!audioUrl || !waveformRef.current) {
        return
      }

      // Create wavesurfer instance
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#888888',
        progressColor: '#0cce94',
        cursorColor: '#333333',
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        height: 80,
        normalize: true,
        url: audioUrl,
      })

      wavesurferRef.current = wavesurfer

      // Handle ready event
      wavesurfer.on('ready', () => {
        setIsReady(true)
      })

      // Handle play/pause events
      wavesurfer.on('play', () => {
        setIsPlaying(true)
        // Call onPlayAudio callback when playback starts
        if (onPlayAudioRef.current) {
          onPlayAudioRef.current()
        }
      })

      wavesurfer.on('pause', () => {
        setIsPlaying(false)
      })

      // Handle playback finish
      wavesurfer.on('finish', () => {
        setIsPlaying(false)
      })

      // Handle waveform interaction - auto-play when clicking to seek
      wavesurfer.on('interaction', () => {
        if (!wavesurfer.isPlaying()) {
          wavesurfer.play()
        }
      })

      // Handle errors - ignore AbortError as it's expected during cleanup when fetch is cancelled
      wavesurfer.on('error', (error) => {
        // AbortError is expected when WaveSurfer is destroyed during audio fetch - ignore it
        if (error?.name === 'AbortError') {
          return
        }
        console.error('Wavesurfer error:', error)
        logSessionInfo('teacherTroubleshoot_AUDIO_PLAYBACK_ERROR', {});
        handleAudioErrorRef.current?.()
      })

      // Cleanup
      return () => {
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy()
          wavesurferRef.current = null
        }
      }
    }, [audioUrl])

    const handlePlayPause = () => {
      logSessionInfo('teacherTroubleshoot_AUDIO_PLAY_PAUSE_CLICKED', {});
      if (wavesurferRef.current) {
        wavesurferRef.current.playPause()
      }
    }

    if (audioError) {
      return (
        <div className="audio-section">
          <div className="audio-section__status-message audio-section__status-message--error">
            We are unable to replay this audio.
          </div>
        </div>
      )
    }

    return (
      <div className="audio-section">
        {audioUrl ? (
          <div className="audio-section__player">
            <button 
              className="audio-section__play-button"
              onClick={handlePlayPause}
              disabled={!isReady}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="white"
                  style={{ width: '22px', height: '22px' }}
                >
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="white"
                  style={{ width: '22px', height: '22px', marginLeft: '2px' }}
                >
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
            <div 
              className="audio-section__waveform" 
              ref={waveformRef}
            />
          </div>
        ) : (
          <div className="audio-section__status-message">
            {isLoading ? 'Loading audio...' : 'Audio not available'}
          </div>
        )}
      </div>
    )
} 